import { useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { Baby, TrendingUp, Package, Users, Heart } from 'lucide-react';

const COLORS = {
  slate: '#475569',
  slateLight: '#94a3b8',
  red: '#dc2626',
  green: '#16a34a',
  blue: '#2563eb',
  amber: '#d97706',
  pink: '#db2777',
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg rounded-lg p-3 text-sm">
      <p className="font-semibold text-gray-900 dark:text-white mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: entry.color }} />
          {entry.name}: <span className="font-medium">{entry.value != null ? entry.value : '-'}</span>
        </p>
      ))}
    </div>
  );
};

export const BirthsChart = ({ data }) => {
  const hasData = data.some(d => d.naissances > 0);
  return (
    <Card className="border-0 shadow-md" data-testid="chart-naissances">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Baby className="w-5 h-5 text-pink-600 dark:text-pink-400" />
          Naissances par mois
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">Aucune donnée de naissance</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="mois" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="naissances" name="Naissances" fill={COLORS.pink} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export const StockConsumptionChart = ({ data }) => {
  const hasData = data.some(d => d.consommation_stock > 0);
  return (
    <Card className="border-0 shadow-md" data-testid="chart-stock">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Package className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          Consommation de stock
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">Aucune consommation enregistrée</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="mois" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="consommation_stock" name="Consommation" stroke={COLORS.amber} fill={COLORS.amber} fillOpacity={0.15} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export const HerdEvolutionChart = ({ data }) => {
  const hasData = data.some(d => d.entrees_troupeau > 0 || d.sorties_troupeau > 0);
  return (
    <Card className="border-0 shadow-md" data-testid="chart-troupeau">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          Évolution du troupeau
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">Aucune donnée de troupeau</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="mois" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="entrees_troupeau" name="Entrées" fill={COLORS.green} radius={[4, 4, 0, 0]} />
              <Bar dataKey="sorties_troupeau" name="Sorties" fill={COLORS.red} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export const RevenueExpenseChart = ({ data, currency }) => {
  const hasData = data.some(d => d.revenus > 0 || d.depenses > 0);
  return (
    <Card className="border-0 shadow-md" data-testid="chart-finances">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          Revenus / Dépenses ({currency})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">Aucune donnée financière</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="mois" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="revenus" name="Revenus" stroke={COLORS.green} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="depenses" name="Dépenses" stroke={COLORS.red} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export const SurvivalRateChart = ({ data }) => {
  const filtered = useMemo(() => data.filter(d => d.taux_survie != null), [data]);
  if (filtered.length === 0) return null;
  return (
    <Card className="border-0 shadow-md" data-testid="chart-survie">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Heart className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          Taux de survie des agneaux
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={filtered}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="mois" tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#94a3b8" unit="%" />
            <Tooltip content={<ChartTooltip />} />
            <Area type="monotone" dataKey="taux_survie" name="Taux de survie (%)" stroke={COLORS.slate} fill={COLORS.slateLight} fillOpacity={0.2} strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
